import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { format } from "date-fns";
import { getPostBySlug } from "../../utils/posts";
import { useReadingProgress } from "../../hooks/usePosts";
import LoadingScreen from "../../components/ui/LoadingScreen";

const ROUTE_MAP = {
  "macro-note":     "insights",
  "weekly-outlook": "weekly-outlook",
  "trade-idea":     "trade-ideas",
  "education":      "education",
};

const CATEGORY_COLORS = {
  "weekly-outlook": "#f5c518",
  "trade-idea":     "#4ade80",
  "macro-note":     "#818cf8",
  "education":      "#f87171",
  "resource":       "#c9a84c",
};

// ─────────────────────────────────────────────────────────────────────────────
// Known section headings per category
// Add new headings here — they are detected automatically on every post page.
// ─────────────────────────────────────────────────────────────────────────────

// Weekly Outlook headings
const WEEKLY_OUTLOOK_HEADINGS = [
  "Market Overview",
  "Central Banks This Week",
  "Oil and Energy Markets",
  "Gold and Safe Haven Assets",
  "Currencies and Dollar Dynamics",
  "Equities and Risk Sentiment",
  "Geopolitical Risk Overlay",
  "Economic Data to Watch",
  "Market Bias and Key Levels",
  "Risks to the View",
];

// Macro Note headings
export const MACRO_NOTE_HEADINGS = [
  "Global Macro Environment",
  "Trade and Tariff Dynamics",
  "Monetary Policy Outlook",
  "Technology and AI Investment Trends",
  "Gold Outlook",
  "Currency Outlook",
  "Euro Outlook",
  "Pound Sterling Outlook",
  "Japanese Yen Outlook",
  "Key Risks to the Global Outlook",
];

// Combined master list — sorted longest-first so the regex splitter always
// matches longer phrases before any shorter phrase that may be a substring.
const KNOWN_HEADINGS = [
  ...MACRO_NOTE_HEADINGS,
  ...WEEKLY_OUTLOOK_HEADINGS,
].sort((a, b) => b.length - a.length);

function formatDate(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return format(d, "MMMM dd, yyyy");
}

// ─────────────────────────────────────────────────────────────────────────────
// FormattedReportContent
//
// Accepts raw plain-text pasted into the admin editor and converts it into
// properly structured HTML:
//
//   • Lines that exactly match a known heading → <h2>
//   • Empty lines                              → paragraph break
//   • Everything else                          → accumulated into <p> blocks
//
// If the content already starts with an HTML tag it is returned as-is so
// any legacy posts stored with manual HTML still render correctly.
// ─────────────────────────────────────────────────────────────────────────────
export function FormattedReportContent({ content, accentColor = "#c9a84c" }) {
  if (!content || !content.trim()) {
    return <p style={{ color: "#444", fontStyle: "italic", fontSize: "16px" }}>No content available.</p>;
  }

  // ── Legacy HTML path ──────────────────────────────────────────────────────
  if (content.trim().startsWith("<")) {
    return (
      <div
        className="article-content"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }

  // ── Smart formatter ───────────────────────────────────────────────────────
  //
  // Handles TWO storage formats:
  //
  //   A) Newline-based (new posts pasted cleanly):
  //        "Market Overview\nParagraph text...\n\nCentral Banks This Week\n..."
  //
  //   B) Inline / no-newline (old posts saved as one long string):
  //        "Market Overview Paragraph text... Central Banks This Week ..."
  //
  // Strategy: build a regex that splits the raw string on any known heading,
  // keeping the heading as a captured group so we know where each section
  // starts. This works regardless of whether newlines are present.
  // ─────────────────────────────────────────────────────────────────────────

  // Sort headings longest-first so longer phrases match before shorter ones
  const sortedHeadings = [...KNOWN_HEADINGS].sort((a, b) => b.length - a.length);

  // Escape any regex special chars in heading text (just in case)
  const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Build a splitter regex: split on headings, capturing them
  const splitter = new RegExp(
    "(" + sortedHeadings.map(escapeRe).join("|") + ")",
    "gi"
  );

  // Split — result is alternating [text?, heading, text, heading, text, ...]
  const parts = content.split(splitter).filter(p => p !== undefined);

  const headingSetLower = new Set(KNOWN_HEADINGS.map(h => h.toLowerCase()));

  const elements = [];
  let keyCounter = 0;

  // Helper: turn a block of prose text into one or more <p> elements.
  // Respects blank lines as hard paragraph breaks; otherwise treats each
  // non-empty run of lines as a single paragraph.
  const flushProse = (block) => {
    const trimmed = block.trim();
    if (!trimmed) return;

    // If the block contains real newlines, split on them
    if (trimmed.includes("\n")) {
      let buffer = [];
      trimmed.split("\n").forEach((rawLine) => {
        const line = rawLine.trim();
        if (line === "") {
          if (buffer.length) {
            elements.push(<p key={`p-${keyCounter++}`}>{buffer.join(" ")}</p>);
            buffer = [];
          }
        } else {
          buffer.push(line);
        }
      });
      if (buffer.length) {
        elements.push(<p key={`p-${keyCounter++}`}>{buffer.join(" ")}</p>);
      }
    } else {
      // No newlines — single paragraph
      elements.push(<p key={`p-${keyCounter++}`}>{trimmed}</p>);
    }
  };

  parts.forEach((part) => {
    if (!part) return;
    if (headingSetLower.has(part.trim().toLowerCase())) {
      // Normalise to the canonical casing from KNOWN_HEADINGS
      const canonical = KNOWN_HEADINGS.find(
        h => h.toLowerCase() === part.trim().toLowerCase()
      ) || part.trim();
      elements.push(<h2 key={`h-${keyCounter++}`}>{canonical}</h2>);
    } else {
      flushProse(part);
    }
  });

  return <div className="article-content">{elements}</div>;
}

// ─────────────────────────────────────────────────────────────────────────────
// PostDetailPage
// ─────────────────────────────────────────────────────────────────────────────
export default function PostDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const progress = useReadingProgress();

  useEffect(() => {
    getPostBySlug(slug)
      .then(p => {
        if (!p) navigate("/404");
        setPost(p);
        setTimeout(() => setVisible(true), 80);
      })
      .catch(() => navigate("/404"))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) return <LoadingScreen />;
  if (!post) return null;

  const backRoute   = ROUTE_MAP[post.category] || "insights";
  const accentColor = CATEGORY_COLORS[post.category] || "#c9a84c";

  return (
    <>
      <Helmet>
        <title>{post.title} — Macro Trader</title>
        <meta name="description" content={post.excerpt} />
        {post.coverImage && <meta property="og:image" content={post.coverImage} />}
      </Helmet>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity:0; transform:translateY(24px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes fadeIn    { from{opacity:0} to{opacity:1} }
        @keyframes lineGrow  { from{transform:scaleX(0)} to{transform:scaleX(1)} }
        @keyframes dotBlink  { 0%,100%{opacity:1} 50%{opacity:0.3} }

        .reading-bar {
          position: fixed; top:0; left:0; height:2px;
          background: linear-gradient(90deg,${accentColor},${accentColor}80);
          box-shadow: 0 0 12px ${accentColor}60;
          z-index: 9999; transition: width 0.1s linear;
        }

        /* ── Paragraph body text ── */
        .article-content p {
          font-family: Georgia, serif;
          font-size: 18px;
          line-height: 2;
          color: #c8c8c8;
          margin-bottom: 2em;
          letter-spacing: 0.01em;
        }

        /* ── Drop-cap on first paragraph ── */
        .article-content > p:first-of-type::first-letter {
          font-size: 4.5rem;
          font-weight: 700;
          float: left;
          line-height: 0.78;
          margin: 6px 14px 0 0;
          color: ${accentColor};
          text-shadow: 0 0 30px ${accentColor}60;
        }

        /* ── Section headings (both manual <h2> and auto-detected) ── */
        .article-content h2 {
          font-family: 'Barlow', sans-serif;
          font-weight: 700;
          font-size: 1.4rem;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #f0f0f0;
          margin: 3em 0 1em;
          padding-bottom: 12px;
          border-bottom: 1px solid #1e1e1e;
          position: relative;
        }
        .article-content h2::after {
          content: '';
          position: absolute; bottom: -1px; left: 0;
          width: 50px; height: 2px;
          background: ${accentColor};
          transform-origin: left;
          animation: lineGrow 0.6s ease forwards;
        }

        /* ── Sub-headings ── */
        .article-content h3 {
          font-family: 'Barlow', sans-serif;
          font-weight: 700; font-size: 1.1rem;
          text-transform: uppercase; letter-spacing: 0.05em;
          color: ${accentColor}; margin: 2em 0 0.8em;
        }

        /* ── Blockquote ── */
        .article-content blockquote {
          border-left: 3px solid ${accentColor};
          padding: 20px 28px; margin: 2.5em 0;
          background: ${accentColor}08; border-radius: 0 8px 8px 0;
          font-family: Georgia, serif;
          font-size: 1.15rem; font-style: italic;
          color: #d0d0d0; line-height: 1.85; position: relative;
        }
        .article-content blockquote::before {
          content: '"'; position: absolute; top: -10px; left: 20px;
          font-size: 4rem; color: ${accentColor}30; line-height: 1;
        }

        /* ── Inline formatting ── */
        .article-content strong { color: #f0f0f0; font-weight: 600; }
        .article-content em    { color: #aaa; font-style: italic; }

        /* ── Lists ── */
        .article-content ul, .article-content ol {
          margin: 1.6em 0; padding-left: 0; list-style: none;
        }
        .article-content li {
          font-family: Georgia, serif;
          font-size: 17px; color: #c8c8c8;
          line-height: 1.9; margin-bottom: 0.8em;
          padding-left: 26px; position: relative;
        }
        .article-content ul li::before {
          content: ''; position: absolute; left: 0; top: 12px;
          width: 6px; height: 6px; border-radius: 50%;
          background: ${accentColor}; box-shadow: 0 0 8px ${accentColor}80;
        }
        .article-content ol { counter-reset: list-counter; }
        .article-content ol li { counter-increment: list-counter; }
        .article-content ol li::before {
          content: counter(list-counter)".";
          position: absolute; left: 0; top: 0;
          color: ${accentColor}; font-family: 'Barlow', sans-serif;
          font-weight: 700; font-size: 13px;
        }

        /* ── Links & images ── */
        .article-content a {
          color: ${accentColor}; text-decoration: underline;
          text-underline-offset: 3px;
        }
        .article-content img {
          width: 100%; border-radius: 8px;
          margin: 2em 0; border: 1px solid #1e1e1e;
        }

        /* ── Tag pills ── */
        .tag-pill {
          font-family: 'DM Mono', monospace; font-size: 10px;
          padding: 4px 12px; border-radius: 20px;
          background: ${accentColor}12; border: 1px solid ${accentColor}28;
          color: ${accentColor}; letter-spacing: 0.12em; text-transform: uppercase;
        }
      `}</style>

      <div className="reading-bar" style={{ width: `${progress}%` }} />

      {/* ── Cover image ── */}
      {post.coverImage && (
        <div style={{ paddingTop: 68, height: "clamp(220px,42vw,460px)", overflow: "hidden", position: "relative" }}>
          <img src={post.coverImage} alt={post.title} style={{ width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.6)" }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom,transparent 30%,#050505)" }} />
        </div>
      )}

      {/* ── HEADER ── */}
      <div style={{ paddingTop: post.coverImage ? 0 : 100, marginTop: post.coverImage ? -100 : 0, paddingBottom: 56, borderBottom: "1px solid #141414", position: "relative", zIndex: 1, background: post.coverImage ? "transparent" : "#050505" }}>
        <div style={{ position: "absolute", top: "30%", left: "20%", width: 500, height: 300, borderRadius: "50%", background: `radial-gradient(ellipse,${accentColor}06 0%,transparent 70%)`, pointerEvents: "none" }} />

        <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px", position: "relative", zIndex: 1 }}>

          <div style={{ opacity: visible ? 1 : 0, animation: visible ? "fadeIn 0.5s ease forwards" : "none" }}>
            <Link
              to={`/${backRoute}`}
              style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "DM Mono,monospace", fontSize: "11px", letterSpacing: "0.16em", textTransform: "uppercase", color: "#444", marginBottom: 36, transition: "color 0.2s", textDecoration: "none" }}
              onMouseEnter={e => e.currentTarget.style.color = accentColor}
              onMouseLeave={e => e.currentTarget.style.color = "#444"}
            >← Back</Link>
          </div>

          <div style={{ marginBottom: 20, opacity: visible ? 1 : 0, animation: visible ? "fadeSlideUp 0.5s 0.1s ease both" : "none" }}>
            <span style={{ fontFamily: "DM Mono,monospace", fontSize: "9px", padding: "5px 14px", borderRadius: "20px", background: accentColor + "15", border: `1px solid ${accentColor}30`, color: accentColor, letterSpacing: "0.18em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: accentColor, display: "inline-block", boxShadow: `0 0 8px ${accentColor}`, animation: "dotBlink 2s ease-in-out infinite" }} />
              {post.category?.replace(/-/g, " ")}
              {post.featured && <span style={{ marginLeft: 8, opacity: 0.7 }}>· Featured</span>}
            </span>
          </div>

          <h1 style={{ fontFamily: "Georgia,serif", fontWeight: 600, fontSize: "clamp(2rem,5vw,3.2rem)", color: "#f0f0f0", lineHeight: 1.2, marginBottom: 20, opacity: visible ? 1 : 0, animation: visible ? "fadeSlideUp 0.6s 0.15s ease both" : "none", textShadow: `0 0 60px ${accentColor}15` }}>
            {post.title}
          </h1>

          {post.excerpt && (
            <p style={{ fontSize: "19px", color: "#777", lineHeight: 1.75, marginBottom: 32, fontFamily: "Georgia,serif", fontStyle: "italic", opacity: visible ? 1 : 0, animation: visible ? "fadeSlideUp 0.6s 0.2s ease both" : "none" }}>
              {post.excerpt}
            </p>
          )}

          <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap", paddingTop: 24, borderTop: "1px solid #141414", opacity: visible ? 1 : 0, animation: visible ? "fadeSlideUp 0.6s 0.25s ease both" : "none" }}>
            <span style={{ fontFamily: "DM Mono,monospace", fontSize: "12px", color: "#444", letterSpacing: "0.08em" }}>{formatDate(post.publishDate || post.createdAt)}</span>
            {post.author && <span style={{ fontFamily: "DM Mono,monospace", fontSize: "12px", color: "#444" }}>By <span style={{ color: accentColor }}>{post.author}</span></span>}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(post.tags || []).map(tag => <span key={tag} className="tag-pill">#{tag}</span>)}
            </div>
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{ background: "#050505", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "20%", right: "-5%", width: 400, height: 600, borderRadius: "50%", background: `radial-gradient(ellipse,${accentColor}04 0%,transparent 70%)`, pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "20%", left: "-5%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(ellipse,#c9a84c04 0%,transparent 70%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 860, margin: "0 auto", padding: "72px 24px 112px", position: "relative", zIndex: 1 }}>
          <div style={{ opacity: visible ? 1 : 0, animation: visible ? "fadeSlideUp 0.7s 0.3s ease both" : "none" }}>
            <FormattedReportContent
              content={post.content || ""}
              accentColor={accentColor}
            />
          </div>

          <div style={{ marginTop: 80, paddingTop: 40, borderTop: `1px solid #141414`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, opacity: visible ? 1 : 0, animation: visible ? "fadeIn 0.6s 0.5s ease both" : "none" }}>
            <Link
              to={`/${backRoute}`}
              style={{ fontFamily: "DM Mono,monospace", fontSize: "12px", letterSpacing: "0.12em", textTransform: "uppercase", color: "#444", textDecoration: "none", transition: "color 0.2s", display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 20px", border: "1px solid #1e1e1e", borderRadius: "6px" }}
              onMouseEnter={e => { e.currentTarget.style.color = accentColor; e.currentTarget.style.borderColor = accentColor + "40"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "#444"; e.currentTarget.style.borderColor = "#1e1e1e"; }}
            >← Back to {backRoute.replace(/-/g, " ")}</Link>
            <p style={{ fontFamily: "DM Mono,monospace", fontSize: "11px", color: "#333", fontStyle: "italic", letterSpacing: "0.06em" }}>Educational purposes only. Not financial advice.</p>
          </div>
        </div>
      </div>
    </>
  );
}
